import { Component } from '@theme/component';

/** Native, section-scoped enhancement for the homepage's catalogue arrangements. */
class SDHomeShowcase extends Component {
  #abort;
  #observer;
  #timer = 0;
  #idle = 0;
  #deferredTimer = 0;
  #swipeTimer = 0;
  #index = 0;
  #hovered = false;
  #focused = false;
  #visible = true;
  #ready = false;
  #paused = false;
  #touchStart;
  #reduce;
  #slides = [];
  #gallery;

  connectedCallback() {
    super.connectedCallback();
    this.#start();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#stop();
    this.#start();
  }

  disconnectedCallback() {
    this.#stop();
    super.disconnectedCallback();
  }

  #stop() {
    this.#abort?.abort();
    this.#observer?.disconnect();
    clearTimeout(this.#timer);
    clearTimeout(this.#deferredTimer);
    clearTimeout(this.#swipeTimer);
    if (this.#idle && 'cancelIdleCallback' in window) cancelIdleCallback(this.#idle);
    this.#idle = 0;
    this.#ready = false;
    this.#touchStart = undefined;
    if (this.#gallery) delete this.#gallery.dataset.swiped;
  }

  #start() {
    this.#gallery = this.querySelector('[data-home-showcase]');
    this.#slides = [...this.querySelectorAll('.gh-showcase-slide')];
    if (!this.#gallery || !this.#slides.length) return;
    this.#abort = new AbortController();
    const signal = this.#abort.signal;
    this.#reduce = matchMedia('(prefers-reduced-motion: reduce)');
    this.#index = Math.min(Number(this.#gallery.dataset.activeSlide) || 0, this.#slides.length - 1);
    this.#hovered = false;
    this.#focused = this.matches(':focus-within');
    this.#visible = true;
    this.#paused = this.dataset.autoplay !== 'true';
    const listen = (target, name, handler, options = {}) => target?.addEventListener(name, handler, { ...options, signal });

    for (const button of this.querySelectorAll('[data-showcase-pause]')) {
      listen(button, 'click', () => { this.#paused = !this.#paused; this.#sync(); });
    }
    listen(this.querySelector('[data-showcase-prev]'), 'click', () => this.#show(this.#index - 1, true));
    listen(this.querySelector('[data-showcase-next]'), 'click', () => this.#show(this.#index + 1, true));
    listen(this.#gallery, 'keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      this.#show(event.key === 'Home' ? 0 : event.key === 'End' ? this.#slides.length - 1 : this.#index + (event.key === 'ArrowRight' ? 1 : -1), true);
    });
    listen(this, 'pointerenter', (event) => { if (event.pointerType === 'mouse') { this.#hovered = true; this.#sync(); } });
    listen(this, 'pointerleave', () => { this.#hovered = false; this.#sync(); });
    listen(this, 'focusin', () => { this.#focused = true; this.#sync(); });
    listen(this, 'focusout', () => queueMicrotask(() => {
      if (signal.aborted) return;
      this.#focused = this.matches(':focus-within');
      this.#sync();
    }));
    listen(this.#gallery, 'pointerdown', (event) => {
      if ((event.pointerType === 'touch' || event.pointerType === 'pen') && !event.target.closest('button')) {
        this.#touchStart = { x: event.clientX, y: event.clientY };
        this.#paused = true;
        this.#sync();
      }
    });
    listen(this.#gallery, 'pointerup', (event) => {
      if (!this.#touchStart) return;
      const dx = event.clientX - this.#touchStart.x;
      const dy = event.clientY - this.#touchStart.y;
      this.#touchStart = undefined;
      if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.4) {
        this.#show(this.#index + (dx < 0 ? 1 : -1), true);
        this.#gallery.dataset.swiped = 'true';
        clearTimeout(this.#swipeTimer);
        this.#swipeTimer = setTimeout(() => delete this.#gallery.dataset.swiped, 120);
      }
    });
    listen(this.#gallery, 'pointercancel', () => { this.#touchStart = undefined; });
    listen(this.#gallery, 'click', (event) => {
      if (this.#gallery.dataset.swiped) { event.preventDefault(); event.stopPropagation(); }
    }, { capture: true });
    listen(document, 'visibilitychange', () => this.#sync());
    listen(this.#reduce, 'change', () => this.#sync());
    this.#observer = new IntersectionObserver((entries) => {
      this.#visible = entries[0]?.isIntersecting ?? false;
      this.#sync();
    }, { threshold: 0.12 });
    this.#observer.observe(this.#gallery);
    const ready = () => {
      this.#ready = true;
      this.#sync();
      const deferred = () => {
        if (!signal.aborted) this.#slides.forEach((slide) => this.#loadSlide(slide));
      };
      if ('requestIdleCallback' in window) this.#idle = requestIdleCallback(deferred, { timeout: 3000 });
      else this.#deferredTimer = setTimeout(deferred, 1800);
    };
    if (document.readyState === 'complete') ready();
    else listen(window, 'load', ready, { once: true });
    this.dataset.ready = 'true';
    this.#show(this.#index, false);
  }

  #loadSlide(slide) {
    for (const element of slide.querySelectorAll('[data-src], [data-srcset]')) {
      if (element.dataset.srcset) { element.setAttribute('srcset', element.dataset.srcset); delete element.dataset.srcset; }
      if (element.dataset.src) { element.setAttribute('src', element.dataset.src); delete element.dataset.src; }
    }
  }

  #show(requested, manual) {
    if (!this.#slides.length) return;
    const old = this.#slides[this.#index];
    const focused = document.activeElement?.closest('.gh-showcase-card');
    const focusIndex = manual && focused && old.contains(focused) ? [...old.querySelectorAll('.gh-showcase-card')].indexOf(focused) : -1;
    this.#index = (requested + this.#slides.length) % this.#slides.length;
    this.#loadSlide(this.#slides[this.#index]);
    const track = this.querySelector('.gh-showcase-track');
    if (track) track.style.transform = `translate3d(${-this.#index * (100 / this.#slides.length)}%, 0, 0)`;
    this.#slides.forEach((slide, index) => { slide.inert = index !== this.#index; slide.setAttribute('aria-hidden', String(index !== this.#index)); });
    const label = this.#slides[this.#index].dataset.label || '';
    const indexLabel = this.querySelector('.gh-showcase-index');
    if (indexLabel) indexLabel.textContent = `${String(this.#index + 1).padStart(2, '0')} / ${String(this.#slides.length).padStart(2, '0')}`;
    const status = this.querySelector('.gh-showcase-status');
    if (status) status.textContent = label;
    this.#gallery.dataset.activeSlide = String(this.#index);
    if (focusIndex >= 0) {
      const cards = this.#slides[this.#index].querySelectorAll('.gh-showcase-card');
      cards[Math.min(focusIndex, cards.length - 1)]?.focus({ preventScroll: true });
    }
    if (manual) {
      this.#paused = true;
      const announcement = this.querySelector('.gh-showcase-announcement');
      if (announcement) announcement.textContent = this.#gallery.dataset.announcement.replace('[current]', String(this.#index + 1)).replace('[total]', String(this.#slides.length)) + ' ' + label;
    }
    this.#sync();
  }

  #sync() {
    clearTimeout(this.#timer);
    const stopped = this.#paused || this.#reduce.matches;
    this.dataset.motionPaused = String(stopped || this.#hovered || this.#focused || !this.#visible || document.hidden);
    for (const button of this.querySelectorAll('[data-showcase-pause]')) {
      button.setAttribute('aria-pressed', String(stopped));
      button.disabled = this.#reduce.matches;
      const text = button.querySelector('[data-motion-label]');
      if (text) text.textContent = this.#reduce.matches ? this.dataset.motionOff : stopped ? this.dataset.resumeLabel : this.dataset.pauseLabel;
    }
    if (this.#ready && this.#slides.length > 1 && !stopped && !this.#hovered && !this.#focused && this.#visible && !document.hidden) {
      this.#timer = setTimeout(() => this.#show(this.#index + 1, false), 7200);
    }
  }
}

if (!customElements.get('sd-home-showcase')) customElements.define('sd-home-showcase', SDHomeShowcase);
